import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type { EdpRecipientView } from '../../api/employeeDocsTypes.js';
import { Alert, Button, Card, Field, TextArea } from '../../components/ui.js';
import { ReturnItemsModal } from '../../employeeDocs/ReturnItemsModal.js';
import { formatBytes, formatDate, formatDateTime } from '../../lib/format.js';

/**
 * A released package as USPS sees it: the same cards, View / Download buttons and
 * ZIP link whether reached from the emailed link or from a signed-in account.
 */
export function ReleasedPackageView({
  view,
  fileUrl,
  archiveUrl,
  acceptUrl,
  returnUrl,
  onChanged,
  onReturned,
  actions,
}: {
  view: EdpRecipientView;
  fileUrl: (fileId: string, inline: boolean) => string;
  archiveUrl: string;
  /** POST target recording USPS's acceptance of the package. */
  acceptUrl: string;
  /** POST target sending chosen documents back to VGS with reasons. */
  returnUrl: string;
  /** Called after acceptance so the caller can refresh the view. */
  onChanged: () => Promise<void> | void;
  /** Called after a return; the package is no longer available to USPS. */
  onReturned: () => Promise<void> | void;
  actions?: ReactNode;
}) {
  const { package: pkg, items } = view;
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () =>
      apiRequest(acceptUrl, {
        method: 'POST',
        body: { note: note.trim() || undefined },
      }),
    onSuccess: () => onChanged(),
    onError: (cause) =>
      setError(
        cause instanceof ApiRequestError ? cause.message : 'The package could not be accepted.',
      ),
  });
  const withDocuments = items.filter((item) => item.files.length > 0);
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{pkg.title}</h2>
            <p className="text-sm text-slate-600">
              {pkg.employeeName} · approved by VGS {formatDateTime(pkg.approvedAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {items.some((item) => item.files.length > 0) && (
              <a
                href={archiveUrl}
                className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
              >
                Download all (ZIP)
              </a>
            )}
            {actions}
          </div>
        </div>
        <Alert variant="info" className="mt-4">
          {pkg.accessExpiresAt
            ? `Your access to this package ends on ${formatDate(pkg.accessExpiresAt)}. `
            : ''}
          Each view and download is recorded. Please store downloaded documents according to your
          handling requirements for personal information.
        </Alert>
      </Card>

      <Card title="Your decision">
        {error && (
          <Alert variant="error" className="mb-3">
            {error}
          </Alert>
        )}
        {pkg.uspsAcceptedAt ? (
          <Alert variant="success" title="Package accepted">
            Accepted {formatDateTime(pkg.uspsAcceptedAt)}
            {pkg.uspsAcceptedBy ? ` by ${pkg.uspsAcceptedBy}` : ''}. VGS can see this on the
            package. If a problem is found later, the documents can still be returned below.
            {pkg.uspsAcceptedNote && (
              <p className="mt-2 whitespace-pre-wrap">
                <span className="font-medium">Your note:</span> {pkg.uspsAcceptedNote}
              </p>
            )}
          </Alert>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              Once you have checked the documents, record your decision. Accepting tells VGS the
              package is complete; returning sends the documents you choose back to VGS with your
              reasons so they can be corrected and re-sent to you.
            </p>
            <Field
              label="Note to VGS (optional)"
              htmlFor="usps-accept-note"
              hint="Saved with your acceptance and shown to VGS on the package."
              className="mt-3"
            >
              <TextArea
                id="usps-accept-note"
                rows={3}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={accept.isPending}
              />
            </Field>
          </>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {!pkg.uspsAcceptedAt && (
            <Button loading={accept.isPending} onClick={() => accept.mutate()}>
              Accept package
            </Button>
          )}
          <Button
            variant="danger"
            disabled={withDocuments.length === 0}
            onClick={() => setReturning(true)}
          >
            Return documents to VGS
          </Button>
        </div>
      </Card>
      {returning && (
        <ReturnItemsModal
          endpoint={returnUrl}
          items={withDocuments}
          title="Return documents to VGS"
          description="Tick each document that is not acceptable and give the reason. VGS reviews the returns first, corrects or has the employee correct them, and sends you a new link once the package is re-released. Your access to this package ends when you return it."
          submitLabel={(count) => `Return ${count || ''} document${count === 1 ? '' : 's'} to VGS`}
          noteLabel="Note to VGS (optional)"
          onClose={() => setReturning(false)}
          onDone={() => {
            setReturning(false);
            return onReturned();
          }}
        />
      )}

      {items.map((item) => (
        <Card key={item.id} title={item.name}>
          {item.files.length === 0 ? (
            <p className="text-sm text-slate-500">No document.</p>
          ) : (
            <ul className="divide-y divide-slate-200 rounded-md ring-1 ring-slate-200">
              {item.files.map((file) => (
                <li
                  key={file.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-slate-900">{file.fileName}</p>
                    <p className="text-xs text-slate-500">{formatBytes(file.byteSize)}</p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={fileUrl(file.id, true)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md px-3 py-1.5 font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
                    >
                      View
                    </a>
                    <a
                      href={fileUrl(file.id, false)}
                      className="rounded-md bg-brand-700 px-3 py-1.5 font-semibold text-white hover:bg-brand-800"
                    >
                      Download
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ))}
    </div>
  );
}
